/**
 * original source: https://ui.shadcn.com/docs/components/avatar
 */
import * as React from 'react'
import { Avatar as AvatarPrimitive } from 'radix-ui'

import { cn, tv, createNonNullableContext } from '@joyful/utils'
import type { VariantProps } from '@joyful/utils'
import type { ComponentProps } from 'react'

const avatarVariants = tv({
  base: 'relative flex shrink-0 overflow-hidden',

  defaultVariants: {
    size: 'md',
    variant: 'round',
  },

  slots: {
    fallback:
      'bg-tremor-brand-subtle text-tremor-content-strong flex size-full items-center justify-center rounded-full pt-0.5 font-serif',
  },

  variants: {
    ring: {
      impersonating:
        'ring-tremor-brand ring-offset-tremor-background ring-2 ring-offset-2',
    },
    size: {
      lg: {
        base: 'size-8',
        fallback: 'text-sm',
      },
      md: {
        base: 'size-6',
        fallback: 'text-xs',
      },
      sm: {
        base: 'size-4',
        fallback: 'text-[0.6rem] leading-[normal] font-semibold',
      },
    },
    variant: {
      round: 'rounded-full',
      square: 'rounded-xs',
    },
  },
})

const avatarFallbackVariants = tv({
  variants: {
    tone: {
      inverse: 'text-white',
      muted: 'bg-tremor-background-subtle',
    },
  },
})

const AvatarClassNamesContext = createNonNullableContext<
  ReturnType<typeof avatarVariants>
>('AvatarClassNamesContext')

export type AvatarProps = Omit<
  ComponentProps<typeof AvatarPrimitive.Root>,
  'className'
> &
  VariantProps<typeof avatarVariants>

function Avatar({ ring, size, variant, ...props }: AvatarProps) {
  const classNames = avatarVariants({
    ring,
    size,
    variant,
  })

  return (
    <AvatarClassNamesContext.Context value={classNames}>
      <AvatarPrimitive.Root
        data-slot="avatar"
        className={classNames.base()}
        {...props}
      />
    </AvatarClassNamesContext.Context>
  )
}

export type AvatarImageProps = Omit<
  ComponentProps<typeof AvatarPrimitive.Image>,
  'className'
>

function AvatarImage(props: AvatarImageProps) {
  return (
    <AvatarPrimitive.Image
      data-slot="avatar-image"
      className="aspect-square size-full"
      {...props}
    />
  )
}

export type AvatarFallbackProps = Omit<
  ComponentProps<typeof AvatarPrimitive.Fallback>,
  'className'
> &
  VariantProps<typeof avatarFallbackVariants>

function AvatarFallback({ tone, ...props }: AvatarFallbackProps) {
  const classNames = AvatarClassNamesContext.useRequiredContext()
  return (
    <AvatarPrimitive.Fallback
      data-slot="avatar-fallback"
      className={cn(classNames.fallback(), avatarFallbackVariants({ tone }))}
      {...props}
    />
  )
}

const avatarGroupVariants = tv({
  base: 'group/avatar-group *:data-[slot=avatar]:ring-tremor-background flex',

  defaultVariants: {
    overlap: 'default',
    size: 'md',
  },

  variants: {
    overlap: {
      default: '-space-x-2',
      loose: '-space-x-1',
      tight: '-space-x-3',
    },
    size: {
      md: '*:data-[slot=avatar]:ring-2',
      sm: '*:data-[slot=avatar]:ring-1',
    },
  },
})

export type AvatarGroupProps = Omit<ComponentProps<'div'>, 'className'> &
  VariantProps<typeof avatarGroupVariants>

function AvatarGroup({ overlap, size, ...props }: AvatarGroupProps) {
  return (
    <div
      data-slot="avatar-group"
      className={avatarGroupVariants({ overlap, size })}
      {...props}
    />
  )
}

export { Avatar, AvatarFallback, AvatarImage, AvatarGroup }
